// StudentTestsPage.js - Full-page tests view for students
import React, { useState, useRef, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
  Platform,
  Linking,
  TextInput,
  Pressable,
  Animated,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getMainContentColors } from "../components/MainContentArea";

const TABLET_BREAKPOINT = 768;
const DESKTOP_BREAKPOINT = 1024;

// ─── Score helpers ────────────────────────────────────────────
const getScoreColor = (score, total) => {
  const pct = (score / total) * 100;
  if (pct >= 80) return "#10b981";
  if (pct >= 60) return "#f59e0b";
  return "#ef4444";
};
const getScoreBgColor = (score, total, dark) => {
  const pct = (score / total) * 100;
  if (pct >= 80) return dark ? "rgba(52,211,153,0.2)" : "rgba(16,185,129,0.1)";
  if (pct >= 60) return dark ? "rgba(251,191,36,0.2)" : "rgba(245,158,11,0.1)";
  return dark ? "rgba(248,113,113,0.2)" : "rgba(239,68,68,0.1)";
};

// ─── Collapsible wrapper ──────────────────────────────────────
const Collapsible = ({ collapsed, children }) => {
  const [contentHeight, setContentHeight] = useState(0);
  const anim = useRef(new Animated.Value(collapsed ? 0 : 1)).current;

  useEffect(() => {
    Animated.timing(anim, {
      toValue: collapsed ? 0 : 1,
      duration: 250,
      useNativeDriver: false,
    }).start();
  }, [collapsed]);

  const height = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, contentHeight || 500],
  });

  const opacity = anim.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, 0, 1],
  });

  return (
    <Animated.View style={{ height: contentHeight ? height : undefined, opacity, overflow: "hidden" }}>
      <View
        onLayout={(e) => {
          const h = e.nativeEvent.layout.height;
          if (h > 0 && h !== contentHeight) setContentHeight(h);
        }}
      >
        {children}
      </View>
    </Animated.View>
  );
};

// ─── Main Component ───────────────────────────────────────────
const StudentTestsPage = ({
  availableTests = [],
  myTestResults = [],
  startStudentTest,
  handleSelectPastTestResult,
  formatLocalDate,
  PDF_BASE_URL,
  isDarkMode = false,
  t = (s) => s,
  // Practice Tests props
  userEmail = "",
  myPracticeTests = [],
  practiceSubjects = [],
  practiceSubjectKey = "",
  setPracticeSubjectKey = () => {},
  practiceSubjectId = "",
  setPracticeSubjectId = () => {},
  practiceSubjectName = "",
  setPracticeSubjectName = () => {},
  practiceChapters = [],
  setPracticeChapters = () => {},
  practiceChapterMode = "all",
  setPracticeChapterMode = () => {},
  practiceSelectedChapterIds = [],
  setPracticeSelectedChapterIds = () => {},
  practiceTitle = "",
  setPracticeTitle = () => {},
  practiceTotal = 10,
  setPracticeTotal = () => {},
  practiceMix = "balanced",
  quickCreatePracticeTest,
  deletePracticeTest,
  refreshMyPracticeTests,
}) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;
  const colors = getMainContentColors(isDarkMode);

  const [createFormOpen, setCreateFormOpen] = useState(false);
  const [subjectDropdownOpen, setSubjectDropdownOpen] = useState(false);

  const openPDF = (pdfPath) => {
    const url = `${PDF_BASE_URL}/${pdfPath}`;
    if (Platform.OS === "web") {
      window.open(url, "_blank");
    } else {
      Linking.openURL(url);
    }
  };

  // ─── Section header helpers ──────────────────────────────
  // AIT-251: Test family uses purple accent + clipboard-text-outline icon
  // (see frontend/app/i18n/glossary.js).
  const renderAvailableTestsHeader = () => (
    <View style={s.sectionHeader}>
      <View style={[
        s.sectionIconBg,
        Platform.OS === "web"
          ? { background: "linear-gradient(135deg, #8B5CF6, #a78bfa)" }
          : { backgroundColor: "#8B5CF6" },
      ]}>
        <MaterialCommunityIcons name="clipboard-text-outline" size={18} color="#fff" />
      </View>
      <Text style={[s.sectionTitle, { color: colors.textPrimary }]}>
        {t("Assigned Tests")}
      </Text>
      {availableTests.length > 0 && (
        <View style={[s.countPill, { backgroundColor: isDarkMode ? "rgba(167,139,250,0.2)" : "rgba(139,92,246,0.1)" }]}>
          <Text style={{ color: "#8B5CF6", fontSize: 12, fontWeight: "700" }}>{availableTests.length}</Text>
        </View>
      )}
    </View>
  );

  const renderPracticeTestsHeader = () => (
    <View style={s.sectionHeader}>
      <View style={[
        s.sectionIconBg,
        Platform.OS === "web"
          ? { background: "linear-gradient(135deg, #10b981, #34d399)" }
          : { backgroundColor: "#10b981" },
      ]}>
        <MaterialCommunityIcons name="clipboard-text-clock" size={18} color="#fff" />
      </View>
      <Text style={[s.sectionTitle, { color: colors.textPrimary }]}>
        {t("My Practice Tests")}
      </Text>
      {myPracticeTests.length > 0 && (
        <View style={[s.countPill, { backgroundColor: isDarkMode ? "rgba(16,185,129,0.2)" : "rgba(16,185,129,0.1)" }]}>
          <Text style={{ color: "#10b981", fontSize: 12, fontWeight: "700" }}>{myPracticeTests.length}</Text>
        </View>
      )}
    </View>
  );

  // ─── Available Tests — hero cards ─────────────────────────
  const renderAvailableTests = (showHeader = true) => (
    <View>
      {showHeader && renderAvailableTestsHeader()}

      {availableTests.length > 0 ? (
        <View style={s.availableGrid}>
          {availableTests.map((test) => (
            <TouchableOpacity
              key={test.id}
              onPress={() => startStudentTest(test.id)}
              activeOpacity={0.85}
              style={[
                s.availableCard,
                {
                  backgroundColor: isDarkMode ? "#1e293b" : "#fff",
                  borderColor: isDarkMode ? "rgba(99,102,241,0.25)" : "rgba(99,102,241,0.15)",
                  ...(Platform.OS === "web" ? {
                    transition: "all 0.25s ease",
                    cursor: "pointer",
                  } : {}),
                },
              ]}
            >
              {/* Top accent bar */}
              <View style={[
                s.availableAccentBar,
                Platform.OS === "web"
                  ? { background: "linear-gradient(90deg, #6366f1 0%, #818cf8 50%, #a5b4fc 100%)" }
                  : { backgroundColor: "#6366f1" },
              ]} />

              <View style={s.availableCardBody}>
                <View style={s.availableCardTop}>
                  <View style={{ flex: 1 }}>
                    <Text style={[s.availableTitle, { color: colors.textPrimary }]} numberOfLines={2}>
                      {test.title}
                    </Text>
                    <Text style={[s.availableMeta, { color: colors.textSecondary }]}>
                      {test.start_time ? formatLocalDate(test.start_time) : t("Available now")}
                    </Text>
                  </View>
                  <View style={[s.assignedBadge, { backgroundColor: isDarkMode ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.08)" }]}>
                    <MaterialCommunityIcons name="school" size={14} color={isDarkMode ? "#a5b4fc" : "#6366f1"} />
                    <Text style={{ color: isDarkMode ? "#a5b4fc" : "#6366f1", fontSize: 11, fontWeight: "600" }}>
                      {t("Assigned")}
                    </Text>
                  </View>
                </View>

                {/* Start action bar */}
                <View style={[
                  s.startBar,
                  Platform.OS === "web"
                    ? { background: isDarkMode ? "linear-gradient(135deg, rgba(99,102,241,0.15), rgba(129,140,248,0.08))" : "linear-gradient(135deg, rgba(99,102,241,0.08), rgba(129,140,248,0.04))" }
                    : { backgroundColor: isDarkMode ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.06)" },
                ]}>
                  <MaterialCommunityIcons name="play-circle" size={22} color={isDarkMode ? "#818cf8" : "#6366f1"} />
                  <Text style={{ color: isDarkMode ? "#818cf8" : "#6366f1", fontSize: 14, fontWeight: "700" }}>
                    {t("Start Test")}
                  </Text>
                  <MaterialCommunityIcons name="arrow-right" size={18} color={isDarkMode ? "#818cf8" : "#6366f1"} style={{ marginLeft: "auto" }} />
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      ) : (
        <View style={[s.emptyHero, { backgroundColor: isDarkMode ? "#1e293b" : "#fff", borderColor: isDarkMode ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.1)" }]}>
          <View style={[s.emptyIconCircle, { backgroundColor: isDarkMode ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.08)" }]}>
            <MaterialCommunityIcons name="clipboard-check-outline" size={36} color={isDarkMode ? "#818cf8" : "#6366f1"} />
          </View>
          <Text style={[s.emptyTitle, { color: colors.textPrimary }]}>{t("All caught up!")}</Text>
          <Text style={[s.emptySubtitle, { color: colors.textSecondary }]}>{t("No tests from your teacher right now.")}</Text>
        </View>
      )}
    </View>
  );

  // ─── My Practice Tests ────────────────────────────────────
  const renderPracticeTests = (showHeader = true) => (
    <View style={[
      s.practiceCard,
      {
        backgroundColor: isDarkMode ? "#1e293b" : "#fff",
        borderColor: isDarkMode ? "rgba(16,185,129,0.25)" : "rgba(16,185,129,0.15)",
      }
    ]}>
      {showHeader && (
        <View style={s.practiceCardHeader}>
          <View style={[
            s.sectionIconBgSmall,
            Platform.OS === "web"
              ? { background: "linear-gradient(135deg, #10b981, #34d399)" }
              : { backgroundColor: "#10b981" },
          ]}>
            <MaterialCommunityIcons name="clipboard-text-clock" size={14} color="#fff" />
          </View>
          <Text style={[s.practiceCardTitle, { color: colors.textPrimary }]}>
            {t("My Practice Tests")}
          </Text>
          {myPracticeTests.length > 0 && (
            <View style={[s.countPill, { backgroundColor: isDarkMode ? "rgba(16,185,129,0.2)" : "rgba(16,185,129,0.1)" }]}>
              <Text style={{ color: "#10b981", fontSize: 12, fontWeight: "700" }}>{myPracticeTests.length}</Text>
            </View>
          )}
        </View>
      )}
      {myPracticeTests.length > 0 ? (
        <View style={{ gap: 8 }}>
          {myPracticeTests.map((pt) => (
            <View key={pt.id} style={[s.practiceItem, { borderColor: isDarkMode ? "rgba(148,163,184,0.1)" : "rgba(148,163,184,0.12)" }]}>
              <TouchableOpacity style={{ flex: 1 }} onPress={() => startStudentTest(pt.id)} activeOpacity={0.7}>
                <Text style={[s.practiceItemTitle, { color: colors.textPrimary }]} numberOfLines={1}>
                  {pt.title || t("Practice Test")}
                </Text>
                <Text style={[s.practiceItemMeta, { color: colors.textSecondary }]}>
                  {pt.duration_minutes ? `${pt.duration_minutes} min` : t("Untimed")}
                  {pt.created_at ? ` · ${formatLocalDate(pt.created_at)}` : ""}
                </Text>
              </TouchableOpacity>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                <TouchableOpacity onPress={() => startStudentTest(pt.id)} style={s.playBtn}>
                  <MaterialCommunityIcons name="play" size={16} color="#10b981" />
                </TouchableOpacity>
                <Pressable
                  onPress={() => {
                    if (deletePracticeTest && refreshMyPracticeTests) {
                      deletePracticeTest(pt.id).then(() => refreshMyPracticeTests());
                    }
                  }}
                  style={s.deleteBtn}
                >
                  <MaterialCommunityIcons name="close" size={14} color={isDarkMode ? "#64748b" : "#94a3b8"} />
                </Pressable>
              </View>
            </View>
          ))}
        </View>
      ) : (
        <View style={s.practiceEmpty}>
          <MaterialCommunityIcons name="lightning-bolt-outline" size={28} color={isDarkMode ? "#475569" : "#cbd5e1"} />
          <Text style={[s.practiceEmptyText, { color: colors.textSecondary }]}>
            {t("No practice tests yet")}
          </Text>
        </View>
      )}
    </View>
  );

  // ─── Create Practice Test (collapsible) ───────────────────
  const renderCreateForm = () => {
    const subjectOk = !!practiceSubjectKey && !!practiceSubjectId;
    const chaptersOk = practiceChapterMode === "all" || practiceSelectedChapterIds.length > 0;
    const canCreate = subjectOk && chaptersOk;

    return (
      <View style={[
        s.createCard,
        {
          backgroundColor: isDarkMode ? "#1e293b" : "#fff",
          borderColor: createFormOpen
            ? (isDarkMode ? "rgba(16,185,129,0.35)" : "rgba(16,185,129,0.25)")
            : (isDarkMode ? "rgba(148,163,184,0.15)" : "rgba(148,163,184,0.12)"),
        }
      ]}>
        {/* Toggle header */}
        <TouchableOpacity
          onPress={() => setCreateFormOpen(!createFormOpen)}
          activeOpacity={0.8}
          style={s.createToggle}
        >
          <View style={[
            s.sectionIconBgSmall,
            Platform.OS === "web"
              ? { background: createFormOpen ? "linear-gradient(135deg, #10b981, #34d399)" : (isDarkMode ? "rgba(148,163,184,0.15)" : "rgba(148,163,184,0.1)") }
              : { backgroundColor: createFormOpen ? "#10b981" : (isDarkMode ? "#334155" : "#e2e8f0") },
          ]}>
            <MaterialCommunityIcons name="plus" size={14} color={createFormOpen ? "#fff" : colors.textSecondary} />
          </View>
          <Text style={[s.createToggleText, { color: createFormOpen ? (isDarkMode ? "#34d399" : "#059669") : colors.textPrimary }]}>
            {t("Create Practice Test")}
          </Text>
          <MaterialCommunityIcons
            name={createFormOpen ? "chevron-up" : "chevron-down"}
            size={20}
            color={colors.textSecondary}
            style={{ marginLeft: "auto" }}
          />
        </TouchableOpacity>

        {/* Collapsible body */}
        <Collapsible collapsed={!createFormOpen}>
          <View style={s.createBody}>
            {practiceSubjects && practiceSubjects.length > 0 ? (
              <>
                <Text style={[s.formLabel, { color: colors.textSecondary }]}>{t("Test Name (optional)")}</Text>
                <TextInput
                  value={practiceTitle}
                  onChangeText={setPracticeTitle}
                  placeholder={t("e.g., Biology – Chapter 3 Practice")}
                  placeholderTextColor={isDarkMode ? "#475569" : "#94a3b8"}
                  style={[s.textInput, {
                    backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc",
                    color: colors.textPrimary,
                    borderColor: isDarkMode ? "#334155" : "#e2e8f0",
                  }]}
                />

                <Text style={[s.formLabel, { color: colors.textSecondary }]}>{t("Subject")}</Text>
                <View style={{ zIndex: 10 }}>
                  <TouchableOpacity
                    activeOpacity={0.8}
                    onPress={() => setSubjectDropdownOpen(!subjectDropdownOpen)}
                    style={[
                      s.subjectTrigger,
                      {
                        backgroundColor: isDarkMode ? "#0f172a" : "#f8fafc",
                        borderColor: subjectDropdownOpen
                          ? "#10b981"
                          : (isDarkMode ? "#334155" : "#e2e8f0"),
                        ...(Platform.OS === "web" ? {
                          transition: "all 0.2s ease",
                          boxShadow: subjectDropdownOpen
                            ? "0 0 0 3px rgba(16,185,129,0.15)"
                            : "none",
                        } : {}),
                      },
                    ]}
                  >
                    {practiceSubjectKey ? (
                      <View style={s.subjectSelected}>
                        <View style={[
                          s.subjectDot,
                          Platform.OS === "web"
                            ? { background: "linear-gradient(135deg, #10b981, #34d399)" }
                            : { backgroundColor: "#10b981" },
                        ]} />
                        <Text style={[s.subjectTriggerText, { color: colors.textPrimary }]} numberOfLines={1}>
                          {practiceSubjects.find((x) => String(x.key) === String(practiceSubjectKey))?.label
                            || practiceSubjects.find((x) => String(x.key) === String(practiceSubjectKey))?.name
                            || t("Select a subject")}
                        </Text>
                      </View>
                    ) : (
                      <Text style={[s.subjectTriggerText, { color: isDarkMode ? "#475569" : "#94a3b8" }]}>
                        {t("Select a subject")}
                      </Text>
                    )}
                    <MaterialCommunityIcons
                      name={subjectDropdownOpen ? "chevron-up" : "chevron-down"}
                      size={20}
                      color={subjectDropdownOpen ? "#10b981" : colors.textSecondary}
                    />
                  </TouchableOpacity>

                  {subjectDropdownOpen && (
                    <View style={[
                      s.subjectMenu,
                      {
                        backgroundColor: isDarkMode ? "#1e293b" : "#fff",
                        borderColor: isDarkMode ? "#334155" : "#e2e8f0",
                        ...(Platform.OS === "web" ? {
                          boxShadow: "0 12px 40px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.08)",
                        } : {
                          shadowColor: "#000",
                          shadowOffset: { width: 0, height: 4 },
                          shadowOpacity: 0.15,
                          shadowRadius: 12,
                          elevation: 8,
                        }),
                      },
                    ]}>
                      <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled showsVerticalScrollIndicator={false}>
                        {practiceSubjects.map((sub, idx) => {
                          const isActive = String(sub.key) === String(practiceSubjectKey);
                          return (
                            <TouchableOpacity
                              key={String(sub.key)}
                              activeOpacity={0.7}
                              onPress={() => {
                                setPracticeSubjectKey(String(sub.key));
                                setPracticeSubjectId(sub.id ?? "");
                                setPracticeSubjectName(sub.name || "");
                                setPracticeChapters(sub.chapters || []);
                                setPracticeChapterMode("all");
                                setPracticeSelectedChapterIds([]);
                                setSubjectDropdownOpen(false);
                              }}
                              style={[
                                s.subjectOption,
                                {
                                  backgroundColor: isActive
                                    ? (isDarkMode ? "rgba(16,185,129,0.12)" : "rgba(16,185,129,0.08)")
                                    : "transparent",
                                  borderBottomColor: idx < practiceSubjects.length - 1
                                    ? (isDarkMode ? "rgba(148,163,184,0.08)" : "rgba(148,163,184,0.1)")
                                    : "transparent",
                                },
                                Platform.OS === "web" ? { cursor: "pointer", transition: "background-color 0.15s ease" } : {},
                              ]}
                            >
                              <View style={[
                                s.subjectOptionDot,
                                {
                                  backgroundColor: isActive ? "#10b981" : (isDarkMode ? "#334155" : "#e2e8f0"),
                                },
                              ]} />
                              <Text
                                style={[
                                  s.subjectOptionText,
                                  {
                                    color: isActive ? "#10b981" : colors.textPrimary,
                                    fontWeight: isActive ? "700" : "500",
                                  },
                                ]}
                                numberOfLines={1}
                              >
                                {sub.label || sub.name}
                              </Text>
                              {isActive && (
                                <MaterialCommunityIcons name="check-circle" size={18} color="#10b981" />
                              )}
                            </TouchableOpacity>
                          );
                        })}
                      </ScrollView>
                    </View>
                  )}
                </View>

                {/* Chapters */}
                {practiceChapters && practiceChapters.length > 0 && (
                  <>
                    <Text style={[s.formLabel, { color: colors.textSecondary }]}>{t("Chapters")}</Text>
                    <View style={s.toggleRow}>
                      {["all", "custom"].map((mode) => (
                        <TouchableOpacity
                          key={mode}
                          onPress={() => {
                            setPracticeChapterMode(mode);
                            if (mode === "all") setPracticeSelectedChapterIds([]);
                          }}
                          style={[
                            s.toggleBtn,
                            practiceChapterMode === mode && s.toggleBtnActive,
                            { borderColor: isDarkMode ? "#334155" : "#e2e8f0" },
                          ]}
                        >
                          <Text style={[s.toggleText, { color: practiceChapterMode === mode ? "#10b981" : colors.textSecondary }]}>
                            {mode === "all" ? t("All") : t("Select")}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                    {practiceChapterMode === "custom" && (
                      <View style={s.checkboxList}>
                        {practiceChapters.map((ch) => {
                          const chId = ch.chapter_id;
                          const isSelected = practiceSelectedChapterIds.includes(chId);
                          return (
                            <TouchableOpacity
                              key={chId}
                              onPress={() => {
                                if (isSelected) {
                                  setPracticeSelectedChapterIds(practiceSelectedChapterIds.filter((id) => id !== chId));
                                } else {
                                  setPracticeSelectedChapterIds([...practiceSelectedChapterIds, chId]);
                                }
                              }}
                              style={s.checkboxRow}
                            >
                              <MaterialCommunityIcons
                                name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
                                size={20}
                                color={isSelected ? "#10b981" : colors.textMuted}
                              />
                              <Text style={[s.checkboxLabel, { color: colors.textPrimary }]}>{ch.chapter_name}</Text>
                            </TouchableOpacity>
                          );
                        })}
                      </View>
                    )}
                  </>
                )}

                {/* Question count */}
                <Text style={[s.formLabel, { color: colors.textSecondary }]}>{t("Questions")}</Text>
                <View style={s.qCountRow}>
                  {[5, 10, 15, 20].map((num) => (
                    <TouchableOpacity
                      key={num}
                      onPress={() => setPracticeTotal(num)}
                      style={[
                        s.qCountBtn,
                        practiceTotal === num && s.qCountBtnActive,
                        { borderColor: isDarkMode ? "#334155" : "#e2e8f0" },
                      ]}
                    >
                      <Text style={[s.qCountText, { color: practiceTotal === num ? "#10b981" : colors.textSecondary }]}>
                        {num}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>

                {/* Create button */}
                <TouchableOpacity
                  onPress={async () => {
                    if (!canCreate || !quickCreatePracticeTest) return;
                    try {
                      const chapter_ids = practiceChapterMode === "custom" ? (practiceSelectedChapterIds || []) : null;
                      const payload = {
                        student_email: userEmail,
                        subject_id: Number(practiceSubjectId),
                        subject_name: practiceSubjectName,
                        title: (practiceTitle || "").trim() || null,
                        chapter_ids: chapter_ids && chapter_ids.length ? chapter_ids : null,
                        total_questions: practiceTotal,
                        difficulty_mix: practiceMix,
                        language_id: 1,
                        timed: false,
                      };
                      const { testId, data } = await quickCreatePracticeTest(payload);
                      if (testId) {
                        await refreshMyPracticeTests?.();
                        setPracticeTitle("");
                        startStudentTest(testId);
                      } else {
                        console.error("Quick practice created but no test id returned:", data);
                      }
                    } catch (e) {
                      console.error("Quick practice failed", e);
                    }
                  }}
                  disabled={!canCreate}
                  style={[
                    s.createBtn,
                    Platform.OS === "web" && canCreate
                      ? { background: "linear-gradient(135deg, #10b981, #059669)", cursor: "pointer" }
                      : {},
                    !canCreate && s.createBtnDisabled,
                    !canCreate && isDarkMode && { backgroundColor: "#334155" },
                  ]}
                >
                  <MaterialCommunityIcons name="lightning-bolt" size={18} color={canCreate ? "#fff" : (isDarkMode ? "#475569" : "#94a3b8")} />
                  <Text style={[s.createBtnText, !canCreate && { color: isDarkMode ? "#475569" : "#94a3b8" }]}>{t("Create & Start")}</Text>
                </TouchableOpacity>
              </>
            ) : (
              <View style={s.practiceEmpty}>
                <Text style={[s.practiceEmptyText, { color: colors.textSecondary }]}>
                  {t("No subjects available for practice right now.")}
                </Text>
              </View>
            )}
          </View>
        </Collapsible>
      </View>
    );
  };

  // ─── Test Results ─────────────────────────────────────────
  const renderResults = () => (
    <View style={s.section}>
      <View style={s.sectionHeader}>
        <View style={[
          s.sectionIconBg,
          Platform.OS === "web"
            ? { background: "linear-gradient(135deg, #f59e0b, #fbbf24)" }
            : { backgroundColor: "#f59e0b" },
        ]}>
          <MaterialCommunityIcons name="chart-box-outline" size={18} color="#fff" />
        </View>
        <Text style={[s.sectionTitle, { color: colors.textPrimary }]}>{t("My Results")}</Text>
      </View>

      {myTestResults.length > 0 ? (
        <View style={[s.resultsGrid, { flexDirection: isDesktop ? "row" : "column", flexWrap: isDesktop ? "wrap" : "nowrap" }]}>
          {myTestResults.map((result) => (
            <TouchableOpacity
              key={result.id}
              onPress={() => handleSelectPastTestResult(result)}
              activeOpacity={0.85}
              style={[
                s.resultCard,
                { backgroundColor: isDarkMode ? "#1e293b" : "#fff" },
                isDesktop && { width: "48%" },
              ]}
            >
              <View style={[s.resultAccent, { backgroundColor: getScoreColor(result.score, result.total_score) }]} />
              <View style={s.resultBody}>
                <View style={s.resultTop}>
                  <Text style={[s.resultTitle, { color: colors.textPrimary }]} numberOfLines={1}>{result.test_title}</Text>
                  <View style={[s.scorePill, { backgroundColor: getScoreBgColor(result.score, result.total_score, isDarkMode) }]}>
                    <Text style={[s.scoreText, { color: getScoreColor(result.score, result.total_score) }]}>
                      {result.score}/{result.total_score}
                    </Text>
                  </View>
                </View>
                <Text style={[s.resultMeta, { color: colors.textSecondary }]}>
                  {result.completed_at ? formatLocalDate(result.completed_at) : "N/A"}
                </Text>
                <View style={s.pdfRow}>
                  {result.pdf_path && (
                    <TouchableOpacity onPress={(e) => { e.stopPropagation(); openPDF(result.pdf_path); }} style={[s.pdfChip, { backgroundColor: isDarkMode ? "rgba(148,163,184,0.12)" : "rgba(100,116,139,0.08)" }]}>
                      <MaterialCommunityIcons name="file-pdf-box" size={14} color={colors.textSecondary} />
                      <Text style={[s.pdfChipText, { color: colors.textSecondary }]}>{t("Submitted")}</Text>
                    </TouchableOpacity>
                  )}
                  {result.graded_pdf_path && (
                    <TouchableOpacity onPress={(e) => { e.stopPropagation(); openPDF(result.graded_pdf_path); }} style={[s.pdfChip, { backgroundColor: isDarkMode ? "rgba(52,211,153,0.12)" : "rgba(16,185,129,0.08)" }]}>
                      <MaterialCommunityIcons name="file-check-outline" size={14} color="#10b981" />
                      <Text style={[s.pdfChipText, { color: "#10b981" }]}>{t("Graded")}</Text>
                    </TouchableOpacity>
                  )}
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      ) : (
        <View style={[s.emptySmall, { backgroundColor: isDarkMode ? "#1e293b" : "#fff" }]}>
          <MaterialCommunityIcons name="chart-timeline-variant" size={28} color={isDarkMode ? "#475569" : "#cbd5e1"} />
          <Text style={[s.emptySubtitle, { color: colors.textSecondary }]}>{t("Complete a test to see results here.")}</Text>
        </View>
      )}
    </View>
  );

  // ─── Layout ───────────────────────────────────────────────
  return (
    <ScrollView
      style={[s.container, { backgroundColor: colors.background }]}
      contentContainerStyle={s.content}
      showsVerticalScrollIndicator={false}
    >
      {/* Header */}
      <View style={s.header}>
        <View style={[
          s.headerIcon,
          Platform.OS === "web"
            ? { background: "linear-gradient(135deg, rgba(99,102,241,0.15), rgba(129,140,248,0.08))" }
            : { backgroundColor: "rgba(99,102,241,0.1)" },
        ]}>
          <MaterialCommunityIcons name="clipboard-text" size={28} color={isDarkMode ? "#818cf8" : "#6366f1"} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={[s.headerTitle, { color: colors.textPrimary }]}>{t("Tests")}</Text>
          <Text style={[s.headerSub, { color: colors.textSecondary }]}>{t("Take tests and review your results")}</Text>
        </View>
      </View>

      {/* Stats */}
      <View style={[s.statsRow, { flexDirection: isDesktop ? "row" : "row" }]}>
        {[
          { icon: "clipboard-clock-outline", value: availableTests.length, label: t("Available"), color: "#3b82f6", bg: isDarkMode ? "rgba(59,130,246,0.12)" : "rgba(59,130,246,0.08)" },
          { icon: "check-decagram", value: myTestResults.length, label: t("Completed"), color: "#10b981", bg: isDarkMode ? "rgba(16,185,129,0.12)" : "rgba(16,185,129,0.08)" },
          { icon: "percent", value: myTestResults.length > 0 ? `${Math.round(myTestResults.reduce((sum, r) => sum + ((r.score / r.total_score) * 100 || 0), 0) / myTestResults.length)}%` : "—", label: t("Avg Score"), color: "#f59e0b", bg: isDarkMode ? "rgba(245,158,11,0.12)" : "rgba(245,158,11,0.08)" },
        ].map((stat, i) => (
          <View key={i} style={[s.statCard, { backgroundColor: isDarkMode ? "#1e293b" : "#fff" }]}>
            <View style={[s.statIconBg, { backgroundColor: stat.bg }]}>
              <MaterialCommunityIcons name={stat.icon} size={18} color={stat.color} />
            </View>
            <Text style={[s.statValue, { color: colors.textPrimary }]}>{stat.value}</Text>
            <Text style={[s.statLabel, { color: colors.textSecondary }]}>{stat.label}</Text>
          </View>
        ))}
      </View>

      {/* Main two-column (desktop) or stacked (mobile) */}
      {isDesktop ? (
        <>
          {/* Section headers row — aligned above columns */}
          <View style={s.columnsHeaderRow}>
            <View style={s.leftCol}>
              {renderAvailableTestsHeader()}
            </View>
            <View style={s.rightCol}>
              {renderPracticeTestsHeader()}
            </View>
          </View>

          {/* Content columns — aligned at top */}
          <View style={s.columnsRow}>
            {/* LEFT — Available Tests (hero) */}
            <View style={s.leftCol}>
              {renderAvailableTests(false)}
            </View>

            {/* RIGHT — Practice Tests + Create */}
            <View style={s.rightCol}>
              {renderPracticeTests(false)}
              <View style={{ height: 16 }} />
              {renderCreateForm()}
            </View>
          </View>
        </>
      ) : (
        <>
          {renderAvailableTests()}
          <View style={{ height: 20 }} />
          {renderPracticeTests()}
          <View style={{ height: 12 }} />
          {renderCreateForm()}
        </>
      )}

      <View style={{ height: 28 }} />
      {renderResults()}
    </ScrollView>
  );
};

// ─── Styles ─────────────────────────────────────────────────
const s = StyleSheet.create({
  container: { flex: 1 },
  content: { padding: 24, paddingBottom: 80 },

  // Header
  header: { flexDirection: "row", alignItems: "center", gap: 14, marginBottom: 20 },
  headerIcon: { width: 52, height: 52, borderRadius: 16, justifyContent: "center", alignItems: "center" },
  headerTitle: { fontSize: 22, fontWeight: "700", marginBottom: 2 },
  headerSub: { fontSize: 13 },

  // Stats
  statsRow: { gap: 10, marginBottom: 24 },
  statCard: {
    flex: 1,
    padding: 14,
    borderRadius: 14,
    alignItems: "center",
    gap: 4,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 6,
    elevation: 1,
  },
  statIconBg: { width: 32, height: 32, borderRadius: 10, justifyContent: "center", alignItems: "center" },
  statValue: { fontSize: 22, fontWeight: "700" },
  statLabel: { fontSize: 11 },

  // Sections
  section: { marginBottom: 24 },
  sectionHeader: { flexDirection: "row", alignItems: "center", gap: 10, marginBottom: 14 },
  sectionIconBg: { width: 32, height: 32, borderRadius: 10, justifyContent: "center", alignItems: "center" },
  sectionIconBgSmall: { width: 26, height: 26, borderRadius: 8, justifyContent: "center", alignItems: "center" },
  sectionTitle: { fontSize: 17, fontWeight: "700" },
  countPill: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },

  // Columns
  columnsHeaderRow: { flexDirection: "row", gap: 20, marginBottom: 14 },
  columnsRow: { flexDirection: "row", gap: 20 },
  leftCol: { flex: 1.1, minWidth: 0 },
  rightCol: { flex: 0.9, minWidth: 0 },


  // Available Tests (hero)
  availableGrid: { gap: 12 },
  availableCard: {
    borderRadius: 16,
    borderWidth: 1,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    elevation: 3,
  },
  availableAccentBar: { height: 4 },
  availableCardBody: { padding: 18 },
  availableCardTop: { flexDirection: "row", justifyContent: "space-between", marginBottom: 14, gap: 12 },
  availableTitle: { fontSize: 16, fontWeight: "600", marginBottom: 4, lineHeight: 22 },
  availableMeta: { fontSize: 13 },
  assignedBadge: { flexDirection: "row", alignItems: "center", gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 8, alignSelf: "flex-start" },
  startBar: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderRadius: 12,
  },

  // Empty hero
  emptyHero: { borderRadius: 16, borderWidth: 1, padding: 40, alignItems: "center", gap: 12 },
  emptyIconCircle: { width: 64, height: 64, borderRadius: 20, justifyContent: "center", alignItems: "center", marginBottom: 4 },
  emptyTitle: { fontSize: 16, fontWeight: "600" },
  emptySubtitle: { fontSize: 13, textAlign: "center" },
  emptySmall: { padding: 28, borderRadius: 14, alignItems: "center", gap: 8 },

  // Practice tests card
  practiceCard: { borderRadius: 16, borderWidth: 1, padding: 16, overflow: "hidden" },
  practiceCardHeader: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 14 },
  practiceCardTitle: { fontSize: 14, fontWeight: "700", flex: 1 },
  practiceItem: { flexDirection: "row", alignItems: "center", paddingVertical: 10, borderBottomWidth: 1, gap: 8 },
  practiceItemTitle: { fontSize: 14, fontWeight: "500", marginBottom: 2 },
  practiceItemMeta: { fontSize: 11 },
  playBtn: {
    width: 30, height: 30, borderRadius: 15, justifyContent: "center", alignItems: "center",
    backgroundColor: "rgba(16,185,129,0.1)",
  },
  deleteBtn: { width: 26, height: 26, borderRadius: 13, justifyContent: "center", alignItems: "center" },
  practiceEmpty: { alignItems: "center", paddingVertical: 20, gap: 6 },
  practiceEmptyText: { fontSize: 13, textAlign: "center" },

  // Create form (collapsible)
  createCard: { borderRadius: 16, borderWidth: 1, overflow: "hidden" },
  createToggle: { flexDirection: "row", alignItems: "center", gap: 8, padding: 14 },
  createToggleText: { fontSize: 14, fontWeight: "600" },
  createBody: { paddingHorizontal: 14, paddingBottom: 16 },

  // Form controls
  formLabel: { fontSize: 12, fontWeight: "500", marginBottom: 5, marginTop: 12 },
  textInput: { height: 42, borderRadius: 10, borderWidth: 1, paddingHorizontal: 12, fontSize: 14 },
  subjectTrigger: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    height: 44,
    borderRadius: 12,
    borderWidth: 1.5,
    paddingHorizontal: 14,
  },
  subjectSelected: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: 8,
  },
  subjectDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  subjectTriggerText: {
    fontSize: 14,
    flex: 1,
  },
  subjectMenu: {
    position: "absolute",
    top: 48,
    left: 0,
    right: 0,
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
    ...(Platform.OS === "web" ? {
      animation: "fadeIn 0.15s ease",
    } : {}),
  },
  subjectOption: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 11,
    paddingHorizontal: 14,
    gap: 10,
    borderBottomWidth: 1,
  },
  subjectOptionDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  subjectOptionText: {
    fontSize: 14,
    flex: 1,
  },
  toggleRow: { flexDirection: "row", gap: 8 },
  toggleBtn: { flex: 1, paddingVertical: 9, borderRadius: 10, borderWidth: 1, alignItems: "center" },
  toggleBtnActive: { backgroundColor: "rgba(16,185,129,0.1)", borderColor: "#10b981" },
  toggleText: { fontSize: 13, fontWeight: "600" },
  checkboxList: { marginTop: 8, gap: 6 },
  checkboxRow: { flexDirection: "row", alignItems: "center", gap: 8, paddingVertical: 2 },
  checkboxLabel: { fontSize: 13, flex: 1 },
  qCountRow: { flexDirection: "row", gap: 8 },
  qCountBtn: { flex: 1, paddingVertical: 9, borderRadius: 10, borderWidth: 1, alignItems: "center" },
  qCountBtnActive: { backgroundColor: "rgba(16,185,129,0.1)", borderColor: "#10b981" },
  qCountText: { fontSize: 14, fontWeight: "600" },
  createBtn: {
    flexDirection: "row", alignItems: "center", justifyContent: "center",
    gap: 8, marginTop: 16, paddingVertical: 13, borderRadius: 12,
    backgroundColor: "#10b981",
  },
  createBtnDisabled: { backgroundColor: "#e2e8f0" }, // overridden inline for dark mode
  createBtnText: { color: "#fff", fontSize: 14, fontWeight: "700" },

  // Results
  resultsGrid: { gap: 10 },
  resultCard: {
    flexDirection: "row", borderRadius: 12, overflow: "hidden", marginBottom: 8,
    shadowColor: "#000", shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.04, shadowRadius: 6, elevation: 1,
  },
  resultAccent: { width: 4 },
  resultBody: { flex: 1, padding: 14 },
  resultTop: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 4 },
  resultTitle: { fontSize: 14, fontWeight: "600", flex: 1 },
  scorePill: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  scoreText: { fontSize: 13, fontWeight: "700" },
  resultMeta: { fontSize: 12, marginBottom: 8 },
  pdfRow: { flexDirection: "row", gap: 6 },
  pdfChip: { flexDirection: "row", alignItems: "center", paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, gap: 4 },
  pdfChipText: { fontSize: 11, fontWeight: "500" },
});

export default StudentTestsPage;
